import React, { useState, useEffect } from 'react';
import { Building, Plus, Edit, Trash2, Globe, Key, Power, PowerOff } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { registrarsAPI } from '../lib/api';
import { useTranslation } from '../lib/i18n';

const Registrars = () => {
  const { t } = useTranslation();
  const [registrars, setRegistrars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingRegistrar, setEditingRegistrar] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    website: '',
    api_key: '',
    api_secret: ''
  });

  useEffect(() => {
    fetchRegistrars();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRegistrars = async () => {
    try {
      setLoading(true);
      const data = await registrarsAPI.getAll();
      setRegistrars(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Registrar getirme hatası:', error);
      toast.error(t('registrars.fetchError') + ': ' + (error.message || t('common.unknownError')));
      setRegistrars([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRegistrar) {
        await registrarsAPI.update(editingRegistrar.id, formData);
        toast.success(t('registrars.registrarUpdated'));
      } else {
        await registrarsAPI.create(formData);
        toast.success(t('registrars.registrarAdded'));
      }
      setShowAddForm(false);
      setShowEditForm(false);
      setEditingRegistrar(null);
      setFormData({
        name: '',
        website: '',
        api_key: '',
        api_secret: ''
      });
      fetchRegistrars();
    } catch (error) {
      console.error('Registrar işlem hatası:', error);
      toast.error(editingRegistrar ? t('registrars.updateError') : t('registrars.addError'));
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setShowEditForm(false);
    setEditingRegistrar(null);
    setFormData({
      name: '',
      website: '',
      api_key: '',
      api_secret: ''
    });
  };

  const handleEdit = (registrar) => {
    setEditingRegistrar(registrar);
    setFormData({
      name: registrar.name || '',
      website: registrar.website || '',
      api_key: registrar.api_key || '',
      api_secret: registrar.api_secret || ''
    });
    setShowAddForm(false);
    setShowEditForm(true);
    // Form açıldığında sayfanın en üstüne kaydır
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (registrarId) => {
    if (!window.confirm(t('registrars.confirmDelete'))) {
      return;
    }

    try {
      await registrarsAPI.delete(registrarId);
      toast.success(t('registrars.registrarDeleted'));
      fetchRegistrars();
    } catch (error) {
      console.error('Registrar silme hatası:', error);
      toast.error(error.response?.data?.error || t('registrars.deleteError'));
    }
  };

  const handleToggleDisabled = async (registrarId, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      await axios.put(`/api/registrars/${registrarId}/toggle-disabled`, {
        is_disabled: newStatus
      });
      
      toast.success(newStatus ? t('registrars.statusDisabled') : t('registrars.statusEnabled'));
      fetchRegistrars();
    } catch (error) {
      console.error('Registrar durum güncelleme hatası:', error);
      toast.error(t('registrars.statusError'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-10 p-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Page Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('registrars.title')}</h1>
            <p className="text-gray-600 dark:text-gray-300 mt-2 text-lg">{t('registrars.subtitle')}</p>
          </div>
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl flex items-center space-x-2 shadow-sm hover:shadow-md transition-all duration-200"
          >
            <Plus className="h-5 w-5" />
            <span className="font-medium">{t('registrars.newRegistrar')}</span>
          </button>
        </div>
      </div>

      {/* Domain Firması Ekleme Formu */}
      {showAddForm && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{t('registrars.addRegistrar')}</h3>
            <button
              onClick={handleCancel}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('registrars.registrarName')} *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="GoDaddy, Namecheap, vs."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('registrars.website')}
                </label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://www.example.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('registrars.apiKey')}
                </label>
                <input
                  type="text"
                  name="api_key"
                  value={formData.api_key}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="API anahtarı (opsiyonel)"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('registrars.apiSecret')}
                </label>
                <input
                  type="password"
                  name="api_secret"
                  value={formData.api_secret}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="API gizli anahtarı (opsiyonel)"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors duration-200"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200"
              >
                {t('registrars.addRegistrar')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Domain Firması Düzenleme Formu */}
      {showEditForm && editingRegistrar && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{t('registrars.editRegistrar')}</h3>
            <button
              onClick={handleCancel}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('registrars.registrarName')} *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="GoDaddy, Namecheap, vs."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('registrars.website')}
                </label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://www.example.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('registrars.apiKey')}
                </label>
                <input
                  type="text"
                  name="api_key"
                  value={formData.api_key}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="API anahtarı (opsiyonel)"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('registrars.apiSecret')}
                </label>
                <input
                  type="password"
                  name="api_secret"
                  value={formData.api_secret}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="API gizli anahtarı (opsiyonel)"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors duration-200"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200"
              >
                {t('common.update')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Domain Firmaları Listesi */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{t('registrars.title')}</h3>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('registrars.registrarName')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('registrars.website')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('registrars.apiStatus')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('registrars.status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('common.createdAt')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('common.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {registrars.map((registrar) => (
                <tr key={registrar.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${registrar.is_disabled ? 'opacity-60' : ''}`}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center ${registrar.is_disabled ? 'bg-gray-100 dark:bg-gray-700' : 'bg-orange-100 dark:bg-orange-900/30'}`}>
                        <Building className={`h-5 w-5 ${registrar.is_disabled ? 'text-gray-400 dark:text-gray-500' : 'text-orange-600 dark:text-orange-400'}`} />
                      </div>
                      <div className="ml-4">
                        <div className={`text-sm font-medium ${registrar.is_disabled ? 'text-gray-500 dark:text-gray-500 line-through' : 'text-gray-900 dark:text-white'}`}>
                          {registrar.name}
                          {registrar.is_disabled && <span className="ml-2 text-xs text-red-600 dark:text-red-400">({t('common.passive')})</span>}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">ID: {registrar.id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {registrar.website ? (
                      <a 
                        href={registrar.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                      >
                        <Globe className="h-4 w-4 mr-2" />
                        {registrar.website}
                      </a>
                    ) : (
                      <span className="text-sm text-gray-500 dark:text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {registrar.api_key && registrar.api_secret ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                          <Key className="h-3 w-3 mr-1" />
                          {t('registrars.apiActive')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300">
                          <Key className="h-3 w-3 mr-1" />
                          {t('registrars.apiNone')}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleToggleDisabled(registrar.id, registrar.is_disabled)}
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                        registrar.is_disabled
                          ? 'bg-red-100 text-red-800 hover:bg-red-200'
                          : 'bg-green-100 text-green-800 hover:bg-green-200'
                      }`}
                      title={registrar.is_disabled ? t('registrars.statusEnabled') : t('registrars.statusDisabled')}
                    >
                      {registrar.is_disabled ? (
                        <>
                          <PowerOff className="h-3 w-3 mr-1" />
                          {t('common.passive')}
                        </>
                      ) : (
                        <>
                          <Power className="h-3 w-3 mr-1" />
                          {t('common.active')}
                        </>
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {new Date(registrar.created_at).toLocaleDateString('tr-TR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(registrar)}
                        className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 transition-colors duration-200"
                        title={t('common.edit')}
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(registrar.id)}
                        className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 transition-colors duration-200"
                        title={t('common.delete')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {registrars.length === 0 && (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
          <Building className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">{t('registrars.noRegistrars')}</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('registrars.noRegistrarsDesc')}</p>
        </div>
      )}
    </div>
  );
};

export default Registrars;
